/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import type { Color } from '@upright/ui/assets/styles/tokens';
import clsx from 'clsx';
import React, { Children, ForwardedRef, forwardRef, ReactElement } from 'react';
import { useFocusRing, mergeProps } from 'react-aria';
import styles from './FocusRing.module.css';

export type Props = {
  /** Child element to apply CSS classes to. */
  children: ReactElement<{ className?: string }>;
  /** CSS class to apply when the element has keyboard focus. */
  focusRingClass?: string;
  /**
   * Whether to show the focus ring when something
   * inside the container element has focus (true), or
   * only if the container itself has focus (false).
   * @default false
   */
  within?: boolean;
  /** Outline offset in px. Defaults to the platform ring's `--focus-ring__offset`. */
  offset?: number;
  /**
   * If [data-focus-visible] has been already
   * set by the fousable parent element (see DocumentationSection.tsx)
   */
  isFocusVisible?: boolean;
  /**
   * Escape hatch for grounds the platform ring cannot serve: a dark surface
   * needs a lighter neutral, and `currentColor` derives the ring from the
   * element's own colour. Leave unset for the platform ring — one neutral on
   * every light surface is the point, so a per-call-site colour needs a reason.
   */
  color?: Color | 'currentColor';
};

/**
 * A utility component that applies a CSS class when an element has keyboard focus.
 * Focus rings are visible only when the user is interacting with a keyboard,
 * not with a mouse, touch, or other input methods.
 */
export const FocusRing = forwardRef(function _FocusRing(props: Props, ref: ForwardedRef<HTMLElement>) {
  const { children, focusRingClass, within, offset, color, isFocusVisible: isFocusVisibleFromProps, ...rest } = props;
  const focusRingProps: Props = { children, focusRingClass, within };
  const { isFocusVisible, focusProps } = useFocusRing(focusRingProps);
  const child = Children.only(children);
  const offsetStyle = typeof offset === 'number' ? { '--ds-focus-ring-offset': `${offset}px` } : undefined;
  const colorStyle =
    color && color !== 'currentColor' ? { '--ds-focus-ring-color': `var(--color__${color})` } : undefined;

  return React.cloneElement(
    child,
    mergeProps(
      child.props,
      focusProps,
      {
        ref,
        style: { ...offsetStyle, ...colorStyle },
        'data-focus-visible': isFocusVisibleFromProps ? 'true' : undefined,
        className: clsx(styles.root, {
          [styles['color-current']]: color === 'currentColor',
          [focusRingClass || styles.focused]: isFocusVisibleFromProps || isFocusVisible,
        }),
      },
      rest,
    ),
  );
});
